import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import {
  KitButton,
  KitFlexCol,
  KitFlexRowCentered,
  KitFlexWrapRowSpaced,
  KitSpinner,
  ThemeColors,
  ThemeConstants,
} from '@chargepoint/cp-toolkit';
import { useHistory } from 'react-router-dom';
import Page from '../components/Page';
import utils from '../common/utils';
import banner from '../images/web-signup-header-min.png';

const BannerImg = styled.img`
  max-width: 100%;
  margin-bottom: ${ThemeConstants.spacing.l}rem;
`;

const QRCodeImg = styled.img`
  max-width: 200px;
`;

const ErrorDiv = styled.div`
  color: ${ThemeColors.red_50};
`;

const Title = styled.h1`
  text-align: center;
  font-weight: normal;
  margin-bottom: ${ThemeConstants.spacing.m}rem;
`;

const Wrapper = styled.div`
  max-width: 600px;
  @media all and (max-width: ${ThemeConstants.breakpoints.md}px) {
    max-width: 300px;
  }
  & > div:first-child {
    margin-left: 17px;
  }
`;

const QRCodeHolder = styled(KitFlexWrapRowSpaced)`
  flex-wrap: nowrap;
  @media all and (max-width: ${ThemeConstants.breakpoints.md}px) {
    flex-wrap: wrap;
  }
  & > div:last-child {
    margin-top: 17px;
  }
`;

const ButtonFlexRowCentered = styled(KitFlexRowCentered)`
  margin-top: ${ThemeConstants.spacing.xl}rem;
  margin-bottom: ${ThemeConstants.spacing.xl}rem;
`;

function MFACodeScan() {
  const { t, i18n } = useTranslation();
  const history = useHistory();
  const [errorMessage, setErrorMessage] = useState(null);
  const [qrCode, setQRCode] = useState('');

  useEffect(() => {
    const loadQRCode = async () => {
      try {
        const requestHeaders = utils.getCommonRequestHeaders(i18n);
        const apiUrl = utils.makeSsoApiUrl('/v1/user/mfa/configure');
        const response = await fetch(apiUrl, {
          method: 'GET',
          credentials: 'include',
          headers: requestHeaders,
        });
        const data = await response.json();
        const { hasOwnProperty } = Object.prototype;
        switch (response.status) {
          case 200:
            setQRCode(data.qr_code);
            break;
          default:
            if (hasOwnProperty.call(data, 'error_message')) {
              setErrorMessage(`Error: [${response.status}] ${data.error_message}`);
            } else if (hasOwnProperty.call(data, 'error')) {
              setErrorMessage(`Error: [${response.status}] ${data.error}`);
            } else {
              setErrorMessage(`Error: [${response.status}] ${response.statusText}`);
            }
            break;
        }
      } catch (err) {
        const unknown = t('error_ids.unknown');
        setErrorMessage(
          t('error_with_message', {
            message: unknown,
          })
        );
        console.error(`Unknown Error: ${err}`);
      }
    };

    loadQRCode();
  }, []);

  let content = <KitSpinner size='s' t={t} />;
  if (errorMessage) {
    content = <ErrorDiv>{errorMessage}</ErrorDiv>;
  } else if (qrCode && qrCode !== '') {
    content = (
      <Wrapper>
        <div>{t('mfa.two_step_verify')}</div>
        <QRCodeHolder>
          <div>
            <QRCodeImg src={qrCode} alt={t('mfa.qr_code')} />
          </div>
          <div>
            <p>{t('mfa.scan_qr_code')}</p>
            <p>{t('mfa.scanned_qa_code')}</p>
          </div>
        </QRCodeHolder>
        <ButtonFlexRowCentered>
          <KitButton
            key='qr_code_next'
            data-qa-id='qr_code_next'
            onClick={() => {
              history.push('/enter-code');
            }}
            variant='primary'
          >
            {t('next')}
          </KitButton>
        </ButtonFlexRowCentered>
      </Wrapper>
    );
  }

  return (
    <Page title={t('login_title')}>
      <KitFlexCol>
        <KitFlexRowCentered>
          <BannerImg src={banner} alt='ChargePoint' />
        </KitFlexRowCentered>
        <Title>{t('login_title')}</Title>
        <KitFlexRowCentered>{content}</KitFlexRowCentered>
      </KitFlexCol>
    </Page>
  );
}

export default MFACodeScan;
